import { isPurchaseRedirect } from 'src/lib/iap/iap.util';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { ProductId } from 'src/replicant/iap';
import { komojuFinishPurchaseSequence } from './providers/komoju';
import { stripeFinishPurchaseSequence } from './providers/stripe';
import { assertNever } from 'src/replicant/utils';
import { atoneCompletePurchase } from './providers/atone';
import StateObserver from 'src/StateObserver';
import {
  trackWebPayClose,
  trackWebPayPurchaseFailure,
  trackWebPayPurchaseSuccess,
} from './analytics/iap.web.pay';
import { incrementSessionStat } from '../stats/stats.modifiers';
import type {
  IAPAnalytics,
  RedirectStateAnalytics,
  RevenueAnalytics,
} from './analytics/analytics.types';
import { NativePurchaseProvider, PurchaseCompleteResult } from './iap.types';
import { ProvisionProductAsyncCallback } from '@play-co/gcinstant';
import { GCInstant } from 'src/lib/gcinstant';
import {
  ValidatePurchaseArgs,
  ValidatePurchaseResult,
} from '@play-co/replicant';
import { isProduct } from 'src/replicant/iap/iap.getters';
import {
  purchaseCompleteSequence,
  purchaseFailedSequence,
} from './iap.after.purchase.sequence';
import { Feature } from 'src/replicant/analytics';
import { toReplicantError } from 'src/replicant/utils/errors';
import {
  getRevenueBasePrice,
  getRevenueGrossAnalytics,
} from './analytics/iap.gross.revenue';

// Extract only the necessary data since it will be stored to state
function extractRedirectStateAnalytics(
  redirectLike: RedirectStateAnalytics,
): RedirectStateAnalytics {
  const {
    elapsedSecondsOpen,
    sessionOpenSequence,
    sessionSuccessSequence,
    instanceSelectSequence,
    sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod,
    hasAgeCheckbox,
  } = redirectLike;

  return {
    elapsedSecondsOpen,
    sessionOpenSequence,
    sessionSuccessSequence,
    instanceSelectSequence,
    sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod,
    hasAgeCheckbox,
  };
}

function calculateElapsedProperties(
  openTimestamp: number,
  methodTimestamp: number,
) {
  const now = StateObserver.now();
  const elapsedMS = now - openTimestamp;
  const elapsedMethodMS = now - methodTimestamp;

  return {
    elapsedSeconds: elapsedMS / 1000,
    elapsedSecondsMethod: elapsedMethodMS / 1000,
  };
}

/**
 * Start open web payment sequence
 */
export async function openWebPurchaseSequence(
  productId: ProductId,
  analytics: {
    feature: string;
    subFeature: string;
  },
): Promise<PurchaseCompleteResult> {
  const openTimestamp = StateObserver.now();

  // Show html popup where user will enter payment details and select payment method
  const popupResult = await openPopupPromise('popupOpenWebPay', {
    productId,
  });

  // Store the method timestamp for recalculation and strip it from analytics
  const result = { ...popupResult };
  const methodTimestamp = result.methodTimestamp;
  delete result.methodTimestamp;

  // TS gets very confused here
  // @ts-ignore
  if (!result.success) {
    // @ts-ignore
    if (result.code === 'CANCELED') {
      trackWebPayClose({
        ...result.analytics,
        ...result.methodAnalytics,
        // Send the internal transaction id if it exists. For now only Atone use this
        internalTransactionId: result.internalTransactionId,
      });

      return Promise.reject({ code: 'USER_INPUT', result });
    } else {
      trackWebPayPurchaseFailure({
        // @ts-ignore
        errorCode: result.code,
        // @ts-ignore
        errorMessage: result.error ?? 'NONE',
        ...result.analytics,
        ...result.methodAnalytics,
        // Send the transactions id if it known
        internalTransactionId: result.internalTransactionId,
        purchaseID: result.purchaseID,
      });

      return Promise.reject(result);
    }
  }

  let purchaseData: PurchaseCompleteResult | null = null;

  try {
    // Finish purchase flow
    switch (result.provider) {
      case 'komoju':
        purchaseData = await komojuFinishPurchaseSequence(
          result.token,
          productId,
          analytics,
          extractRedirectStateAnalytics({
            ...result.analytics,
            ...result.methodAnalytics!,
          }),
          methodTimestamp!,
        );
        break;
      case 'stripe':
        purchaseData = await stripeFinishPurchaseSequence(
          result.paymentMethod,
          productId,
          result.purchaseID,
        );
        break;
      case 'atone':
        purchaseData = await atoneCompletePurchase(
          productId,
          result.purchaseID,
          result.transactionType,
        );
        break;
      default:
        assertNever(result);
    }
  } catch (e: any) {
    const methodData = result.methodAnalytics!;
    trackWebPayPurchaseFailure({
      errorCode: e.code === 'paymentCancelled' ? 'USER_INPUT' : e.code,
      errorMessage: e.message,
      ...result.analytics,
      ...methodData,
      ...calculateElapsedProperties(openTimestamp, methodTimestamp!),
      // Send the transactions id if it known
      internalTransactionId: result.internalTransactionId,
      purchaseID: result.purchaseID,
    });

    return Promise.reject({
      ...e,
      provider: methodData.purchaseProvider,
      providerMethod: methodData.purchaseProviderMethod,
    });
  }

  // Purchase was successful here
  if (!isPurchaseRedirect(purchaseData.response)) {
    await StateObserver.invoke.setLastPaymentMethod(
      purchaseData.revenue.purchaseProviderMethod,
    );

    await StateObserver.invoke.incrementPersistentStat(
      'webPayPurchaseSequence',
    );
    incrementSessionStat('webPayPurchaseSequence');

    await StateObserver.invoke.incrementPersistentStat(
      `${result.methodAnalytics!.purchaseProviderMethod}_sequence`,
    );
    incrementSessionStat(
      `${result.methodAnalytics!.purchaseProviderMethod}_sequence`,
    );

    trackWebPayPurchaseSuccess({
      ...result.analytics,
      ...result.methodAnalytics!,
      ...calculateElapsedProperties(openTimestamp, methodTimestamp!),
      // Send the transactions id if it known
      internalTransactionId: result.internalTransactionId,
      purchaseID: purchaseData.purchaseID,
      // Adding 1 to consider the current purchase
      allTimeSuccessSequence: result.analytics.allTimeSuccessSequence + 1,
      allTimeSuccessSequenceForMethod:
        result.methodAnalytics!.allTimeSuccessSequenceForMethod + 1,
      sessionSuccessSequence: result.analytics.sessionSuccessSequence + 1,
      sessionSuccessSequenceForMethod:
        result.methodAnalytics!.sessionSuccessSequenceForMethod + 1,
    });
  }

  return purchaseData;
}

/**
 * Provision product sequence
 */
export const provisionProduct: ProvisionProductAsyncCallback = async (
  purchase,
  product,
  opts,
): Promise<ValidatePurchaseResult> => {
  const os = GCInstant.legacyNative?.platform.os;
  if (!os) return { ok: false, errorCode: 'verificationFailed' };

  // Apple validation also requires the transactionID/token
  // Since app store receipts can contain many transactions
  const validationArgs: ValidatePurchaseArgs = {
    paymentProcessor: os,
    signedRequest: purchase.signedRequest,
    transactionId: purchase.token,
  };

  const revenueData = {
    revenueGrossLocal: purchase.revenueGrossLocal,
    revenueGrossUSD: purchase.revenueGrossUSD,
  };

  const result = await StateObserver.invoke.asyncHandlePurchase({
    validationArgs,
    revenueData,
    purchase: {
      currency: purchase.currency,
      price: purchase.revenueGrossLocal,
      productId: purchase.productID,
    },
    purchaseID: purchase.id,
  });

  // Cast product ID to ProductId type, it will be validated below.
  const productID = product?.id;

  // Validate product ID.
  // This branch should never be reached because asyncHandlePurchase validates product IDs.
  if (!isProduct(productID)) {
    // Show error popup. Don't wait for it to close.
    void purchaseFailedSequence(
      {
        feature: Feature.IAP._,
        subFeature: Feature.IAP.PROVISION_PRODUCT,
        // This is not a valid product ID, we cast so analytics events include the invalid ID.
        productID: productID as ProductId,
      },
      'fatal',
    );

    return {
      ok: false,
      errorCode: 'verificationFailed',
    };
  }

  // Purchase could not be consumed. Let the user know and rethrow the error.
  if (!result.ok) {
    // Show error popup. Don't wait for it to close.
    void purchaseFailedSequence(
      {
        feature: Feature.IAP._,
        subFeature: Feature.IAP.PROVISION_PRODUCT,
        productID,
      },
      'fatal',
    );

    return result;
  }

  // Purchase should not be consumed at launch. Rewards will be displayed separately. Bail out.
  if (!opts.consumedAtLaunch) {
    return { ok: true };
  }

  // Detect base price from purchase data
  const basePrice = {
    price: purchase.revenueGrossLocal,
    currency: purchase.currency,
  };

  // Calculate gross revenue analytics
  const revenueAnalytics: RevenueAnalytics =
    os === 'ios'
      ? getRevenueGrossAnalytics(productID, {
          provider: 'apple',
          providerMethod: 'apple_native',
          ...basePrice,
        })
      : getRevenueGrossAnalytics(productID, {
          provider: 'google',
          providerMethod: 'google_native',
          ...basePrice,
        });

  // Analytics for the purchase
  const iapAnalytics: IAPAnalytics = {
    feature: Feature.IAP._,
    subFeature: Feature.IAP.PROVISION_PRODUCT,
    ...revenueAnalytics,
    purchaseCountBefore: StateObserver.replicant.getPurchaseHistory().length,
    purchaseID: purchase.id,
  };

  // Complete purchase
  await purchaseCompleteSequence(productID, iapAnalytics);

  // Resolve ValidatePurchaseResult
  return { ok: true };
};
